import React, { useEffect, useRef, useState } from 'react'
import throttle from 'lodash.throttle'
import { selectors } from '@sketch/constants'
import {
  Route,
  RouteChildrenProps,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom'
import {
  routes,
  useGetUnreadNotificationsCount,
  WorkspaceSidebarLayoutExtraProps,
} from '@sketch/modules-common'
import { useLocalStorage, useResizableSidePanel } from '@sketch/utils'

import {
  EmptyPreview,
  EmptyPreviewIcon,
  EmptyPreviewText,
  EmptyPreviewFilterCount,
  HeaderActions,
  InboxSearchSlot,
  ListHeaderWrapper,
  Wrapper,
  ListAndHeaderWrapper,
  ListHeaderText,
  UpdatePreview,
  InboxResizeHandle,
  VerticalDivider,
} from './Inbox.styles'
import { InboxList } from './InboxList'
import { InboxPreview } from '../../components/InboxPreview'
import { Filter, Modal, Pluralize, useForDesktop } from '@sketch/components'

import { InboxSortMenu } from './InboxSortMenu'
import { InboxMarkAllAsReadButton } from './InboxMarkAllAsReadButton'
import { Portal } from 'react-portal'
import { useInboxSearchContext } from './InboxSearchContext'
import { useFilteredUnreadNotificationsCount } from './useFilteredUnreadNotificationsCount'
import { InboxCommandBarRegistration } from './commandBar'

const INBOX_LIST_WIDTH_KEY = 'inbox-list-width'
const INBOX_LIST_DEFAULT_WIDTH = 450
const INBOX_LIST_MIN_WIDTH = 320
const INBOX_LIST_MAX_WIDTH = 720
const SCROLLED_THRESHOLD_PX = 4
const SCROLL_THROTTLE_MS = 100

export type InboxViewProps = RouteChildrenProps &
  WorkspaceSidebarLayoutExtraProps

const InboxContent = (props: InboxViewProps) => {
  const { useOverrideLayoutProps } = props
  const { search, setSearch, options } = useInboxSearchContext()
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const isExpanded = isSearchExpanded || !!search
  const unreadCount = useGetUnreadNotificationsCount()
  const { hasActiveFilters, count: filteredUnreadCount } =
    useFilteredUnreadNotificationsCount()
  // Unread notifications hidden by the active filter — the rest of the inbox.
  const excludedUnreadCount = Math.max(0, unreadCount - filteredUnreadCount)
  const isDesktop = useForDesktop()
  const history = useHistory()

  const itemMatch = useRouteMatch<{ notificationId: string }>({
    path: routes.UPDATES_ITEM.template(),
    exact: true,
  })

  const activeNotificationId = itemMatch?.params?.notificationId ?? null

  // Changing a filter or the search query can push the selected notification
  // out of the list, so clear the selection (back to the empty preview)
  // whenever the filters or search change.
  const previousFiltersRef = useRef({ options, search })
  useEffect(() => {
    const previous = previousFiltersRef.current
    if (previous.options === options && previous.search === search) return
    previousFiltersRef.current = { options, search }

    if (activeNotificationId) {
      history.replace(routes.UPDATES.create({}))
    }
  }, [options, search, activeNotificationId, history])

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false)

  // Give the sticky header a background once the list has scrolled, mirroring
  // the workspace `HeaderSlim` behavior. On mobile the inner panel has no
  // `max-height`, so the page-level container does the scrolling instead.
  useEffect(() => {
    const scrollSource = isDesktop
      ? containerRef.current
      : (document.querySelector(
          selectors.contentScrollable
        ) as HTMLElement | null)
    if (!scrollSource) return

    const handleScroll = throttle(() => {
      setIsHeaderScrolled(scrollSource.scrollTop > SCROLLED_THRESHOLD_PX)
    }, SCROLL_THROTTLE_MS)

    handleScroll()
    scrollSource.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      handleScroll.cancel()
      scrollSource.removeEventListener('scroll', handleScroll)
    }
  }, [isDesktop])

  const [storedWidth, setStoredWidth] = useLocalStorage<number>(
    INBOX_LIST_WIDTH_KEY,
    INBOX_LIST_DEFAULT_WIDTH
  )
  const { width, resizeHandleRef } = useResizableSidePanel({
    initialWidth: storedWidth,
    defaultWidth: INBOX_LIST_DEFAULT_WIDTH,
    minWidth: INBOX_LIST_MIN_WIDTH,
    maxWidth: INBOX_LIST_MAX_WIDTH,
    // The list panel is inset within the workspace layout (workspace sidebar
    // sits to its left), so we anchor the drag math to the panel's own left
    // edge rather than the viewport's.
    getOffsetLeft: () =>
      containerRef.current?.getBoundingClientRect().left ?? 0,
    // Don't trample the workspace sidebar's `--sidebar-width` variable.
    cssVariable: null,
    onWidthUpdate: setStoredWidth,
  })

  useOverrideLayoutProps({
    title: 'Inbox',
    hasNavbar: false,
    hideFooter: true,
    hideHeader: true,
    hideMargin: true,
  })

  const closeModalPreview = () => history.push(routes.UPDATES.create({}))

  return (
    <Wrapper>
      <InboxCommandBarRegistration />
      <ListAndHeaderWrapper
        ref={containerRef}
        style={isDesktop ? { width } : undefined}
      >
        <ListHeaderWrapper $scrolled={isHeaderScrolled}>
          <ListHeaderText>Inbox</ListHeaderText>

          <HeaderActions $hidden={isExpanded}>
            <InboxMarkAllAsReadButton />
            <VerticalDivider />
            <InboxSortMenu />
          </HeaderActions>

          <InboxSearchSlot $compact={!isExpanded}>
            <Filter
              collapsible
              fullWidth
              value={search}
              onChange={setSearch}
              onClear={() => setSearch('')}
              onExpandedChange={setIsSearchExpanded}
            />
          </InboxSearchSlot>
        </ListHeaderWrapper>

        <InboxList />
        {isDesktop && <InboxResizeHandle ref={resizeHandleRef} />}
      </ListAndHeaderWrapper>

      {isDesktop ? (
        <UpdatePreview>
          <Switch>
            <Route
              path={routes.UPDATES_ITEM.template()}
              render={({ match }) => {
                const notificationId = (
                  match.params as { notificationId?: string }
                ).notificationId
                if (!notificationId) return null

                return <InboxPreview notificationId={notificationId} />
              }}
            />

            <Route
              render={() => (
                <EmptyPreview>
                  <EmptyPreviewIcon />
                  {hasActiveFilters ? (
                    <>
                      <Pluralize
                        singular="unread notification"
                        count={filteredUnreadCount}
                        showCount
                        wrapperElement={EmptyPreviewText}
                      />
                      {excludedUnreadCount > 0 && (
                        <Pluralize
                          singular="unread notification excluded by filter"
                          plural="unread notifications excluded by filter"
                          count={excludedUnreadCount}
                          showCount
                          wrapperElement={EmptyPreviewFilterCount}
                        />
                      )}
                    </>
                  ) : (
                    <Pluralize
                      singular="Unread Notification"
                      count={unreadCount}
                      showCount
                      wrapperElement={EmptyPreviewText}
                    />
                  )}
                </EmptyPreview>
              )}
            />
          </Switch>
        </UpdatePreview>
      ) : (
        <Route
          path={routes.UPDATES_ITEM.template()}
          render={({ match }) => {
            const notificationId = (match.params as { notificationId?: string })
              .notificationId
            if (!notificationId) return null

            return (
              <Portal>
                <Modal onCancel={closeModalPreview} cancelOnClickOutside>
                  <InboxPreview notificationId={notificationId} />
                </Modal>
              </Portal>
            )
          }}
        />
      )}
    </Wrapper>
  )
}

export const Inbox = (props: InboxViewProps) => <InboxContent {...props} />
